import React, { useEffect, useState, useCallback } from 'react';

/**
 * Minimalist Swiss-style carousel overlay for a service's photo gallery.
 * Props:
 *  - service: { title, detailedDescription, gallery: [url, ...] }
 *  - onClose: () => void
 */
const ServiceCarousel = ({ service, onClose }) => {
  const [index, setIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);

  const total = service?.gallery?.length || 0;

  const next = useCallback(() => {
    if (total <= 1) return;
    setIndex((i) => (i + 1) % total);
  }, [total]);

  const prev = useCallback(() => {
    if (total <= 1) return;
    setIndex((i) => (i - 1 + total) % total);
  }, [total]);

  // Lock body scroll + keyboard nav
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose, next, prev]);

  // Touch swipe
  const handleTouchStart = (e) => setTouchStartX(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX;
    if (delta > 50) prev();
    else if (delta < -50) next();
    setTouchStartX(null);
  };

  if (!service) return null;

  return (
    <div
      data-testid="service-carousel-overlay"
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex flex-col animate-[fadeIn_0.25s_ease-out]"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between px-6 md:px-10 pt-6 md:pt-8">
        <div className="text-white max-w-2xl">
          <p className="text-[11px] tracking-[0.2em] uppercase text-white/60 mb-2">
            Nos réalisations
          </p>
          <h3 className="text-xl md:text-2xl font-semibold">{service.title}</h3>
        </div>
        <button
          data-testid="service-carousel-close"
          onClick={onClose}
          aria-label="Fermer"
          className="text-white/80 hover:text-white transition-colors p-2 -mr-2"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Image stage */}
      <div
        className="flex-1 flex items-center justify-center px-4 md:px-10 py-6 relative"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Prev */}
        {total > 1 && (
          <button
            data-testid="service-carousel-prev"
            onClick={prev}
            aria-label="Photo précédente"
            className="hidden md:flex absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 items-center justify-center w-12 h-12 text-white/70 hover:text-white hover:bg-white/5 rounded-full transition-colors"
          >
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        <img
          data-testid="service-carousel-image"
          key={index}
          src={service.gallery[index]}
          alt={`${service.title} - Photo ${index + 1}`}
          className="max-h-[60vh] md:max-h-[65vh] max-w-full object-contain animate-[fadeIn_0.35s_ease-out]"
        />

        {/* Next */}
        {total > 1 && (
          <button
            data-testid="service-carousel-next"
            onClick={next}
            aria-label="Photo suivante"
            className="hidden md:flex absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 items-center justify-center w-12 h-12 text-white/70 hover:text-white hover:bg-white/5 rounded-full transition-colors"
          >
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      {/* Footer: description + thumbnails + dots */}
      <div className="px-6 md:px-10 pb-6 md:pb-8 max-w-5xl w-full mx-auto">
        {service.detailedDescription && (
          <p className="text-white/75 text-sm md:text-[15px] leading-relaxed mb-5 max-w-3xl">
            {service.detailedDescription}
          </p>
        )}

        {/* Mobile prev/next */}
        {total > 1 && (
          <div className="flex md:hidden items-center justify-between mb-4">
            <button
              data-testid="service-carousel-prev-mobile"
              onClick={prev}
              className="text-white/70 hover:text-white text-sm tracking-wide"
            >
              ← Précédent
            </button>
            <span className="text-white/50 text-xs tabular-nums">
              {index + 1} / {total}
            </span>
            <button
              data-testid="service-carousel-next-mobile"
              onClick={next}
              className="text-white/70 hover:text-white text-sm tracking-wide"
            >
              Suivant →
            </button>
          </div>
        )}

        {/* Thumbnails */}
        {total > 1 && (
          <div className="hidden md:flex gap-2 overflow-x-auto">
            {service.gallery.map((src, i) => (
              <button
                key={i}
                data-testid={`service-carousel-thumb-${i}`}
                onClick={() => setIndex(i)}
                aria-label={`Aller à la photo ${i + 1}`}
                className={`flex-shrink-0 w-20 h-14 overflow-hidden transition-all duration-200 ${
                  i === index ? 'ring-2 ring-white opacity-100' : 'opacity-50 hover:opacity-80'
                }`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceCarousel;
